import React, { useState, useEffect } from 'react';
import theme from '../../theme/theme';
import { Api } from '../../network/api';
import check from '../../assets/images/check.png';

const { Colors } = theme;

const PlansManagement = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeOnly, setActiveOnly] = useState(true);
  const [processingPriceId, setProcessingPriceId] = useState(null);
  const [mySubscription, setMySubscription] = useState(null);

  useEffect(() => {
    fetchPlans();
    fetchMySubscription();
  }, [activeOnly]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await Api.getPlans(activeOnly);
      if (response.data && response.data.plans) {
        const sortedPlans = [...response.data.plans].sort((a, b) => 
          (a.display_order || 0) - (b.display_order || 0)
        );
        setPlans(sortedPlans);
      } else {
        setPlans([]);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load plans. Please try again.');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMySubscription = async () => {
    try {
      const response = await Api.getMySubscription();
      if (response.data && response.data.subscription) {
        setMySubscription(response.data.subscription);
      } else {
        setMySubscription(null);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setMySubscription(null);
    }
  };

  const formatCurrency = (amount, currency = 'usd') => {
    if (!amount) return '$0.00';
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    });
    return formatter.format(amount);
  };

  const formatPrice = (plan) => {
    if (!plan.prices || plan.prices.length === 0) return { amount: 'N/A', interval: '' };
    const activePrices = plan.prices.filter(p => p.active);
    const price = activePrices.length > 0 ? activePrices[0] : plan.prices[0];
    
    const amount = formatCurrency(price.amount, price.currency);
    const interval = price.interval_count === 1 
      ? `/${price.interval}` 
      : `/${price.interval_count} ${price.interval}s`;
    
    return { amount, interval };
  };

  const handleSubscribe = async (priceId) => {
    if (!priceId || processingPriceId) return;
    
    try {
      setProcessingPriceId(priceId);
      const response = await Api.createCheckoutSession(priceId);
      if (response?.data?.url) {
        window.location.href = response?.data?.url;
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Failed to create checkout session. Please try again.');
      setProcessingPriceId(null);
    }
  };

  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: Colors.background }}
    >
      <div className="relative max-w-7xl mx-auto px-4 py-20">
        <div className="mb-14">
          <h2
            className="text-[28px] text-left xl:text-5xl font-extrabold bg-clip-text text-transparent pb-8"
            style={{
              backgroundImage: `linear-gradient(to right, ${Colors.primary}, ${Colors.secondary})`,
            }}
          >
            Plans Management
          </h2>
          {/* <div className="flex justify-center mt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium" style={{ color: Colors.textPrimary }}>
                Active Only
              </span>
            </label>
          </div> */}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: Colors.primary }}></div>
            <span className="ml-2" style={{ color: Colors.textPrimary }}>Loading plans...</span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <h3 className="mt-2 text-sm font-medium" style={{ color: Colors.textPrimary }}>
              Error loading plans
            </h3>
            <p className="mt-1 text-sm" style={{ color: Colors.textSecondary }}>{error}</p>
            <button
              onClick={fetchPlans}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors duration-200 hover:opacity-90"
              style={{
                backgroundImage: `linear-gradient(to right, ${Colors.primary}, ${Colors.secondary})`,
              }}
            >
              Retry
            </button>
          </div>
        ) : plans.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="mt-2 text-sm font-medium" style={{ color: Colors.textPrimary }}>
              No plans found
            </h3>
            <p className="mt-1 text-sm" style={{ color: Colors.textSecondary }}>
              {activeOnly 
                ? 'No active plans available. Try unchecking "Active Only" to see all plans.'
                : 'No plans available in the system.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {plans.map((plan, idx) => {
              const primaryPrice = plan.prices && plan.prices.length > 0
                ? plan.prices.find(p => p.active) || plan.prices[0]
                : null;
              
              const isCurrentPlan = mySubscription?.status === 'active' && 
                mySubscription?.price_id === primaryPrice?.id;
              
              const priceInfo = formatPrice(plan);
              
              return (
                <div
                  key={plan.id}
                  className={`relative border rounded-2xl shadow-xl p-8 flex flex-col justify-between bg-white hover:-translate-x-0 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 hover:ring-4`}
                  style={{
                    borderColor: plan.active ? Colors.primary : Colors.border,
                    animationDelay: `${idx * 0.2}s`,
                  }}
                >
                  {isCurrentPlan && (
                    <span
                      className="absolute top-0 right-0 text-white text-xs font-semibold px-3 py-1 rounded-bl-lg rounded-tr-2xl animate-pulse"
                      style={{
                        backgroundImage: `linear-gradient(to right, ${Colors.primary}, ${Colors.secondary})`,
                      }}
                    >
                      Active
                    </span>
                  )}
                  <div>
                    <h3
                      className="text-[18px] md:text-[22px] lg:text-2xl font-semibold mb-3 text-left"
                      style={{ color: Colors.textPrimary }}
                    >
                      {plan.name}
                    </h3>
                    <div className="mb-3 text-left">
                      <span
                        className="text-[18px] xl:text-4xl font-extrabold bg-clip-text text-transparent"
                        style={{
                          backgroundImage: `linear-gradient(to right, ${Colors.primary}, ${Colors.secondary})`,
                        }}
                      >
                        {priceInfo.amount}
                      </span>
                      {/* {priceInfo.interval && (
                        <span
                          className="text-[14px] xl:text-lg font-semibold ml-1"
                          style={{ color: Colors.textPrimary }}
                        >
                          {priceInfo.interval}{plan.plan_type === 'one_time' ? ' days' : ''}
                        </span>
                      )} */}
                    </div>
                    <p className="mb-6 text-left" style={{ color: Colors.textPrimary }}>
                      {plan.description}
                    </p>
                    {plan.features && plan.features.length > 0 && (
                      <ul className="space-y-3 text-sm mb-8">
                        {plan.features
                          .filter(feature => feature.included)
                          .map((feature, featureIdx) => (
                            <li
                              key={featureIdx}
                              className="flex items-center space-x-2 group relative"
                              style={{ color: Colors.textPrimary }}
                            >
                              <img src={check} alt="checkIcon" width="12px" height="12px" />
                              <span>{feature.name}</span>
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>

                  {/* Bottom section: Allowed Essays + button, always together */}
                  <div className="mt-4">
                    {plan.allowed_essays && (
                      <div className="mb-3 text-sm text-black">
                        <span className="font-medium">Allowed Essays: </span>
                        {plan.allowed_essays}
                      </div>
                    )}

                    <button
                      onClick={() => primaryPrice && handleSubscribe(primaryPrice.id)}
                      disabled={!primaryPrice || processingPriceId === primaryPrice.id || isCurrentPlan}
                      className="relative w-full font-semibold py-3 px-5 rounded-xl shadow-md overflow-hidden transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        backgroundImage: `linear-gradient(to right, ${Colors.primary}, ${Colors.secondary})`,
                        color: Colors.textSecondary,
                      }}
                    >
                      <span className="relative z-10">
                        {processingPriceId === primaryPrice?.id 
                          ? 'Processing...' 
                          : isCurrentPlan 
                          ? 'Current Plan' 
                          : 'Buy Now'}
                      </span>
                      <span className="absolute inset-0 bg-white opacity-0 hover:opacity-10 transition duration-500 rounded-xl"></span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default PlansManagement;

